import * as React from 'react';

import { cn } from '@heidi/utils';

import type { SpotlightBeaconProps } from './spotlight.types';

/**
 * SpotlightBeacon - Adds a subtle pulsing glow animation to draw user attention.
 *
 * The animation is applied to the child element via CSS, so the box-shadow
 * automatically respects the child's border-radius. Works with any shape.
 *
 * @example
 * ```tsx
 * <Spotlight>
 *   <SpotlightAnchor asChild>
 *     <SpotlightBeacon>
 *       <Button>New Feature</Button>
 *     </SpotlightBeacon>
 *   </SpotlightAnchor>
 *   <SpotlightContent>...</SpotlightContent>
 * </Spotlight>
 * ```
 */
const SpotlightBeacon = React.forwardRef<HTMLSpanElement, SpotlightBeaconProps>(
  ({ 'data-testid': testId, className, children, active = false, ...props }, ref) => (
    <span
      ref={ref}
      data-testid={testId}
      className={cn(
        'inline-flex',
        active &&
          '[&>*]:animate-beacon-glow [&>*]:will-change-[box-shadow] [&>*]:motion-reduce:animate-none',
        className
      )}
      {...props}
    >
      {children}
    </span>
  )
);
SpotlightBeacon.displayName = 'SpotlightBeacon';

export { SpotlightBeacon };
